import React, { useState, useEffect } from 'react';
import { Col, Input, Row } from 'reactstrap';
import moment from 'moment';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { VideoClip } from '../SaveVideoClipPopup/SaveVideoClipPopup';
import './VideoClipTile.scss';
import createNotification from '../../../../utils/createNotification';
import { CurrentUser } from '../../../../slices/current-user/current-user';
import { useStateSelector } from '../../../../store/selectors';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { PmgNotificationFlagsHelper } from '../../../../utils/notification-flags-helpers/pmgNotificationFlags.helper';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { UpdateVideoClipPopup } from '../UpdateVideoClipPopup/UpdateVideoClipPopup';

interface VideoClipTileProps {
    groupId?: number;
    engagementId?: number;
    clip: VideoClip;
    onStatusChanged(clipId: number, isShared: boolean): void;
    onViewRecording?(clip: VideoClip, onComplete: () => void): void;
    isShared: boolean;
    shareCheckboxText?: string;
    onShareCheckboxChange?: (clipId: number, isShared: boolean) => void;
    isShareButtonHidden?: boolean;
    onClipUpdated?: () => void;
    type?: 'group' | 'engagement-meetings' | 'marketplace-meetings';
}

const VideoClipTile = (props: VideoClipTileProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const currentUser = useStateSelector(
        (state) => state.currentUser.user
    ) as CurrentUser;
    const [isLoading, setIsLoading] = useState(false);
    const [localIsShared, setLocalIsShared] = useState(props.clip.isShared);
    const [isEditPopupVisible, setIsEditPopupVisible] = useState(false);

    useEffect(() => {
        setLocalIsShared(props.clip.isShared);
    }, [props.clip.isShared]);

    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        let result = '';

        if (hours > 0) {
            result += `${hours}h `;
        }

        if (minutes > 0) {
            result += `${minutes}m `;
        }

        if (remainingSeconds > 0 || result === '') {
            result += `${remainingSeconds}s`;
        }

        return result.trim();
    };

    const updateSharingStatus = () => {
        const newStatus = !localIsShared;
        setLocalIsShared(newStatus);

        if (props.onShareCheckboxChange) {
            props.onShareCheckboxChange(props.clip.id, newStatus);
        } else {
            if (!props.groupId) {
                createNotification('Group ID is required', 'error');
                setLocalIsShared(!newStatus);
                return;
            }
            axios
                .post(
                    `api/groups/${props.groupId}/clips/${props.clip.id}/sharingStatus`,
                    {
                        conferenceId: props.clip.conferenceId,
                        clipId: props.clip.id,
                        isShared: newStatus,
                    }
                )
                .then((response: any) => {
                    if (response.status === 200) {
                        props.onStatusChanged(props.clip.id, newStatus);
                    } else {
                        createNotification(
                            response.data?.message ??
                                'Unexpected error occured on updating Sharing Status',
                            'error'
                        );
                        setLocalIsShared(!newStatus);
                    }
                })
                .catch(() => {
                    setLocalIsShared(!newStatus);
                });
        }
    };

    const getNotificationFlag = () => {
        if (!props.groupId) {
            return null;
        }
        return (
            <NotificationFlag
                flagClassName="nf-flag"
                eventCode={`${PmgNotificationFlagsHelper.generateClubRoomNotificationFlag(
                    props.groupId
                )}.${NotificationFlagsCategories.ClipCode}.${
                    props.clip.id
                }`}></NotificationFlag>
        );
    };

    const handleClipUpdated = () => {
        setIsEditPopupVisible(false);
        if (props.onClipUpdated) {
            props.onClipUpdated();
        }
    };

    return (
        <div className="video-clip-tile">
            {props.isShared ? getNotificationFlag() : []}
            <div className="conference-header">
                <div>{props.clip.title}</div>
                {currentUser.id === props.clip.creatorId && (
                    <FontAwesomeIcon
                        icon={faEdit}
                        className="edit-clip-icon"
                        onClick={() => setIsEditPopupVisible(true)}
                    />
                )}
            </div>
            <div className="conference-info">
                <div>
                    <Row>
                        <Col md={12}>
                            <p className="label-text mb-2 mt-2">
                                Clip Description:
                            </p>
                            <p className="clip-description">
                                {props.clip.description}
                            </p>
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={5}>
                            <div>
                                <span className="label-text">Duration:</span>{' '}
                            </div>
                        </Col>
                        <Col md={5} className="md-value">
                            {formatDuration(
                                props.clip.timeEndSeconds -
                                    props.clip.timeStartSeconds
                            )}
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={5}>
                            <div>
                                <span className="label-text">
                                    Meeting Title:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={7} className="md-value">
                            {props.clip.conferenceName}
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={5}>
                            <div>
                                <span className="label-text">
                                    Meeting Date:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={5} className="md-value">
                            {moment(props.clip.conferenceDate).format(
                                'MM/DD/YYYY'
                            )}
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={12}>
                            <p className="label-text mb-2 mt-2">
                                Meeting Description:
                            </p>
                            <label className="message">
                                {props.clip.conferenceDescription}
                            </label>
                        </Col>
                    </Row>
                    {currentUser.id == props.clip.creatorId &&
                    !props.isShareButtonHidden ? (
                        <Row className="row-space">
                            <Col md={12}>
                                <label className="checkbox-container">
                                    {props.shareCheckboxText ||
                                        'Share this clip with my PMG'}
                                    <Input
                                        name={`sharedStatus`}
                                        type="checkbox"
                                        checked={localIsShared}
                                        onChange={updateSharingStatus}
                                    />
                                    <span className="checkmark"></span>
                                </label>
                            </Col>
                        </Row>
                    ) : (
                        []
                    )}
                </div>

                <Row className="row-space">
                    <Col md={12}>
                        {props.onViewRecording && (
                            <ButtonLoader
                                buttonText={'View Recording'}
                                loaderButtonText={''}
                                disabled={false}
                                isLoading={isLoading}
                                onClick={() => {
                                    setIsLoading(true);
                                    props.onViewRecording(props.clip, () =>
                                        setIsLoading(false)
                                    );
                                }}
                                className="btn btn-primary"></ButtonLoader>
                        )}
                    </Col>
                </Row>
            </div>
            <UpdateVideoClipPopup
                clip={props.clip}
                groupId={props.groupId}
                engagementId={props.engagementId}
                meetingId={props.clip.conferenceId}
                isVisible={isEditPopupVisible}
                onClose={() => setIsEditPopupVisible(false)}
                onClipUpdated={handleClipUpdated}
                type={props.type || 'group'}
            />
        </div>
    );
};

export default VideoClipTile;
